import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/format';

const FREE_LABEL = { zona: 'zona con envío gratis', productos: 'productos con envío gratis', monto_minimo: 'compra superior al mínimo', free_from: 'promoción de envío gratis' };
const QUOTED = ["configured", "per_product", "automarket"];

/** Cotiza el envío por vendedor y reporta {total, blocked, ready} al padre. */
export const ShippingQuote = ({ items, department, city, onChange, nonce = 0 }) => {
  const [rows, setRows] = useState(null);
  const key = JSON.stringify([items.map((x) => [x.product_id, x.qty]), department, city, nonce]);

  useEffect(() => {
    let ok = true;
    setRows(null);
    onChange({ total: 0, blocked: false, ready: false });
    if (!city) return undefined;
    api.post('/shipping/quote', {
      items: items.map((x) => ({ product_id: x.product_id, qty: x.qty })), department, city,
    }).then(({ data }) => {
      if (!ok) return;
      const sellers = data.sellers || [];
      setRows(sellers);
      const needsDept = sellers.some((s) => QUOTED.includes(s.mode)) && !department;
      onChange({
        total: sellers.reduce((s, r) => s + (r.amount || 0), 0),
        blocked: needsDept || sellers.some((s) => !s.covered),
        legacy: sellers.some((s) => s.mode === 'legacy'),
        ready: true,
      });
    }).catch(() => ok && setRows([]));
    return () => { ok = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!city) return <div className="text-xs text-slate-500" data-testid="shipping-quote-hint">Ingresa departamento y ciudad para calcular el envío.</div>;
  if (!rows) return <div className="text-xs text-slate-500">Calculando envío…</div>;
  return (
    <div className="space-y-1 text-sm" data-testid="shipping-quote">
      {rows.map((r) => (
        <div key={r.seller_id} className="flex items-start justify-between gap-2" data-testid={`shipping-quote-${r.seller_id}`}>
          <span className="text-slate-600">
            Envío · {r.seller_name || 'Vendedor'}
            {r.covered && QUOTED.includes(r.mode) && (
              <span className="block text-[11px] text-slate-500">
                {r.carrier || 'Envío del vendedor'}{r.days_max != null ? ` · ${r.days_min}–${r.days_max} días` : ''}{r.free_reason ? ` · gratis por ${FREE_LABEL[r.free_reason]}` : ''}
              </span>
            )}
            {r.mode === 'legacy' && <span className="block text-[11px] text-amber-700">Envío a convenir con el vendedor · no incluido en el total</span>}
          </span>
          {r.mode === 'legacy' && <span className="text-slate-500 text-xs">A convenir</span>}
          {QUOTED.includes(r.mode) && !department && (
            <span className="text-amber-700 text-xs font-semibold text-right" data-testid={`shipping-needs-department-${r.seller_id}`}>Selecciona el departamento</span>
          )}
          {QUOTED.includes(r.mode) && department && (r.covered
            ? <span className="font-semibold text-slate-900">{r.amount ? formatCOP(r.amount) : 'Gratis'}</span>
            : <span className="text-rose-700 text-xs font-semibold text-right" data-testid={`shipping-not-covered-${r.seller_id}`}>No envía a {city}</span>)}
        </div>
      ))}
      {rows.some((r) => r.mode === 'legacy') && (
        <div className="text-xs text-amber-800 border border-amber-200 bg-amber-50 p-2" data-testid="shipping-legacy-alert">
          El costo de envío de los vendedores marcados “a convenir” no está incluido en el total; lo acuerdas directamente con ellos.
        </div>
      )}
      {department && rows.some((r) => !r.covered) && (
        <div className="text-xs text-rose-700 border border-rose-200 bg-rose-50 p-2" data-testid="shipping-not-covered-alert">
          Uno o más vendedores no realizan envíos a tu destino. Retira sus productos o cambia la dirección.
        </div>
      )}
    </div>
  );
};
